import { tableColumnWidths } from '@/features/shared/components/tableSystem/tableColumnWidths';
import { TableMetricsBreakdownCell } from '@/features/shared/components/tableSystem/TableMetricsBreakdownCell';
import { TableStatusBreakdownCell } from '@/features/shared/components/tableSystem/TableStatusBreakdownCell';
import { resolveEntityTags } from '@/features/shared/tags';
import { mapChipKeyToStatusTone } from '@/features/tests/components/testsTableShared';
import { formatDuration, formatFrequency, formatPercent } from '@/ui';
import {
  getDateFilterOperators,
  getNumericFilterOperators,
  getSingleSelectFilterOperators,
} from '@/ui/composites/grid/gridOperators';
import { getThresholdChipKey } from '@/ui/composites/table';
import { mapTestExecutionRunSummaries } from '@/ui/composites/table/tableUtils';
import { type GridColDef } from '@/ui/primitives';
import { buildCoreColumns } from './testsColumns.coreColumns';
import { buildHiddenFilterColumns } from './testsColumns.hiddenFilterColumns';
import { TableActionsCell, TableCellContainer } from './testsTableCells';
import type { TestsColumnsOptions } from './testsColumns.types';
import type { MetricThresholdGroup, TestListItemDto } from '@flowqa/shared';

const columnWidths = tableColumnWidths.tests;

export const buildTestsColumns = ({
  selectedProjectId,
  onProjectSelect,
  onStatusSelect,
  thresholds,
  resolveThresholds,
  showSuiteLinks,
  combineNameAndSuite = false,
  includeSuiteInNameColumn = true,
  includeTagsInNameColumn = false,
  includeActionsColumn = false,
  disableColumnResizeHandles = false,
  latestAsMeta = false,
  includeLastExecutedColumn = true,
  showDeltas = true,
  disableLinks = false,
  metricSeriesByTestId: _metricSeriesByTestId,
  filterSuites = [],
  filterTags = [],
  onDeleteRow,
  deleteRowPendingId = null,
  tagColorByName = {},
  onUpdateRowTagsColor,
  updateRowTagsColorPendingId = null,
}: TestsColumnsOptions): Array<GridColDef<TestListItemDto>> => {
  const singleSelectOperators = getSingleSelectFilterOperators();
  const dateOperators = getDateFilterOperators();
  const numericOperators = getNumericFilterOperators();
  const deltasEnabled = showDeltas;
  const resolvedThresholds = (row: TestListItemDto): MetricThresholdGroup => {
    if (selectedProjectId) return thresholds;
    return resolveThresholds(row) ?? thresholds;
  };

  const { nameColumn, suiteColumn, latestSummaryColumn, lastExecutedColumn } = buildCoreColumns({
    onProjectSelect,
    onStatusSelect,
    showSuiteLinks,
    combineNameAndSuite,
    includeSuiteInNameColumn,
    includeTagsInNameColumn,
    latestAsMeta,
    includeLastExecutedColumn,
    disableLinks,
    tagColorByName,
    columnFlex: {
      name: columnWidths.name.flex,
      suite: columnWidths.suite.flex,
      latest: columnWidths.latestStatus.flex,
      lastExecuted: columnWidths.lastExecuted.flex,
    },
  });

  const metricsColumn: GridColDef<TestListItemDto> = {
    field: 'metricsSummary',
    headerName: 'Metrics',
    flex: columnWidths.metrics.flex,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const passRate = params.row.metrics?.passRate ?? null;
      const passRateDelta = deltasEnabled ? (params.row.metrics?.delta?.passRate ?? null) : null;
      const frequency = params.row.metrics?.frequencyPerDay ?? null;
      const frequencyDelta = deltasEnabled
        ? (params.row.metrics?.delta?.frequencyPerDay ?? null)
        : null;
      const avgDuration = params.row.metrics?.averageDurationMs ?? null;
      const avgDurationDelta = deltasEnabled
        ? (params.row.metrics?.delta?.averageDurationMs ?? null)
        : null;
      const resolvedPassRate = passRate;
      const resolvedPassRateDelta = resolvedPassRate === null ? null : passRateDelta;
      const resolvedFrequency = frequency;
      const resolvedFrequencyDelta = resolvedFrequency === null ? null : frequencyDelta;
      const resolvedAvgDuration = avgDuration;
      const resolvedAvgDurationDelta = resolvedAvgDuration === null ? null : avgDurationDelta;
      const thresholdGroup = resolvedThresholds(params.row);
      const passRateTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedPassRate, thresholdGroup.passRate),
      );
      const frequencyTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedFrequency, thresholdGroup.frequency),
      );
      const avgDurationTone = mapChipKeyToStatusTone(
        getThresholdChipKey(resolvedAvgDuration, thresholdGroup.avgDuration),
      );

      const passRateLabel =
        resolvedPassRate === null ? 'No data' : `${formatPercent(resolvedPassRate)}%`;
      const passRateDeltaLabel =
        resolvedPassRateDelta === null
          ? null
          : resolvedPassRateDelta === 0
            ? 'No change'
            : `${resolvedPassRateDelta > 0 ? '+' : ''}${formatPercent(resolvedPassRateDelta)}%`;
      const frequencyLabel =
        resolvedFrequency === null ? formatFrequency(0) : formatFrequency(resolvedFrequency);
      const frequencyDeltaLabel =
        resolvedFrequencyDelta === null
          ? null
          : resolvedFrequencyDelta === 0
            ? 'No change'
            : `${resolvedFrequencyDelta > 0 ? '+' : resolvedFrequencyDelta < 0 ? '-' : ''}${formatFrequency(
                Math.abs(resolvedFrequencyDelta),
              )}`;
      const avgDurationLabel =
        resolvedAvgDuration === null ? 'No data' : formatDuration(resolvedAvgDuration);
      const avgDurationDeltaLabel =
        resolvedAvgDurationDelta === null
          ? null
          : resolvedAvgDurationDelta === 0
            ? 'No change'
            : `${resolvedAvgDurationDelta > 0 ? '+' : '-'}${formatDuration(
                Math.abs(resolvedAvgDurationDelta),
                { roundMillisecondsWithSeconds: true },
              )}`;

      return (
        <TableCellContainer gap="none">
          <TableMetricsBreakdownCell
            items={[
              {
                key: 'passRate',
                label: 'Pass Rate',
                valueLabel: passRateLabel,
                deltaValue: resolvedPassRateDelta,
                deltaLabel: passRateDeltaLabel,
                isMissing: resolvedPassRate === null,
                tone: passRateTone,
                direction: thresholdGroup.passRate.direction,
              },
              {
                key: 'frequency',
                label: 'Frequency',
                valueLabel: frequencyLabel,
                deltaValue: resolvedFrequencyDelta,
                deltaLabel: frequencyDeltaLabel,
                isMissing: resolvedFrequency === null,
                tone: frequencyTone,
                direction: thresholdGroup.frequency.direction,
              },
              {
                key: 'avgDuration',
                label: 'Avg. Duration',
                valueLabel: avgDurationLabel,
                deltaValue: resolvedAvgDurationDelta,
                deltaLabel: avgDurationDeltaLabel,
                isMissing: resolvedAvgDuration === null,
                tone: avgDurationTone,
                direction: thresholdGroup.avgDuration.direction,
              },
            ]}
          />
        </TableCellContainer>
      );
    },
  };

  const executionsColumn: GridColDef<TestListItemDto> = {
    field: 'executionStatusCounts',
    headerName: 'Executions',
    flex: columnWidths.executions.flex,
    align: 'left',
    headerAlign: 'left',
    filterable: false,
    sortable: false,
    cellClassName: 'table-breakdown-cell',
    renderCell: (params) => {
      const counts = params.row.executionStatusCounts;

      return (
        <TableCellContainer gap="none">
          <TableStatusBreakdownCell
            entity="execution"
            counts={{
              passed: counts?.passed ?? 0,
              failed: counts?.failed ?? 0,
              errored: counts?.errored ?? 0,
              skipped: counts?.skipped ?? 0,
              untested: counts?.untested ?? 0,
            }}
            summaryItems={mapTestExecutionRunSummaries(params.row.executionSummaries)}
            summaryUnitLabel="test"
            totalLinkMode="execution"
          />
        </TableCellContainer>
      );
    },
  };

  const { suiteFilterColumn, durationFilterColumn, timestampFilterColumn, statusFilterColumn } =
    buildHiddenFilterColumns({
      singleSelectOperators,
      numericOperators,
      dateOperators,
      filterSuites,
      filterTags,
    });

  const columns: Array<GridColDef<TestListItemDto>> = [
    nameColumn,
    ...(combineNameAndSuite ? [suiteFilterColumn] : showSuiteLinks ? [suiteColumn] : []),
    latestSummaryColumn,
    ...(latestAsMeta ? [executionsColumn] : []),
    ...(latestAsMeta ? [metricsColumn] : []),
    ...(lastExecutedColumn ? [lastExecutedColumn] : []),
    ...(latestAsMeta ? [statusFilterColumn, durationFilterColumn, timestampFilterColumn] : []),
    ...(includeActionsColumn
      ? [
          {
            field: 'actions',
            headerName: '',
            flex: columnWidths.actions.flex,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
              <TableActionsCell
                rowName={params.row.name}
                rowTags={resolveEntityTags(params.row)}
                rowTagColors={resolveEntityTags(params.row).map(
                  (tag) => tagColorByName[tag] ?? 'neutral',
                )}
                onUpdateTagsColor={(input) => {
                  onUpdateRowTagsColor?.({ row: params.row, ...input });
                }}
                onDelete={() => {
                  onDeleteRow?.(params.row);
                }}
                disableDelete={deleteRowPendingId === params.row.id}
                updateTagsColorLoading={updateRowTagsColorPendingId === params.row.id}
              />
            ),
          } satisfies GridColDef<TestListItemDto>,
        ]
      : []),
  ];

  if (!disableColumnResizeHandles) {
    return columns;
  }

  return columns.map((column) => ({
    ...column,
    resizable: false,
  }));
};
